<template>
  <div class="flex">
    <loading v-if="isLoading" />
    <ConfirmModal
      v-model="showModal"
      message="Are you sure you want to submit this invoice?"
      @confirm="submitFormData"
    />
    <Dialog
      v-model:visible="showPrefill"
      header="Set Form Prefill"
      closable="true"
      modal="true"
    >
      <form class="grid grid-cols-2 md:grid-cols-4 gap-6">
        <!-- General Info Section -->
        <div class="col-span-2 border-r pr-6">
          <div class="text-lg font-semibold mb-3 border-b pb-2 tracking-wide">
            General Information
          </div>

          <div class="grid grid-cols-2 gap-4">
            <!-- Invoice Number -->
            <div>
              <label class="text-sm font-medium mb-1 block"
                >Invoice Number</label
              >
              <input
                v-model="prefillPayload.invoice_number"
                placeholder="Invoice Number"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- Date -->
            <div>
              <label class="text-sm font-medium mb-1 block">Date</label>
              <input
                type="date"
                v-model="prefillPayload.invoice_date"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- Sold To -->
            <div>
              <label class="text-sm font-medium mb-1 block">Sold To</label>
              <input
                v-model="prefillPayload.soldTo"
                placeholder="Sold To"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- TIN -->
            <div>
              <label class="text-sm font-medium mb-1 block">TIN</label>
              <input
                v-model="prefillPayload.tin"
                placeholder="TIN No. Or 0 if not applicable"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- Address -->
            <div class="col-span-2">
              <label class="text-sm font-medium mb-1 block">Address</label>
              <textarea
                v-model="prefillPayload.address"
                rows="3"
                placeholder="Address"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Payment Info Section -->
        <div class="col-span-2 pl-6">
          <div class="text-lg font-semibold mb-3 border-b pb-2 tracking-wide">
            Payment Information
          </div>

          <div class="grid grid-cols-2 gap-4">
            <!-- Mode of Payment -->
            <div>
              <label class="text-sm font-medium mb-1 block"
                >Mode of Payment</label
              >
              <select
                v-model="prefillPayload.modeOfPayment"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              >
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
              </select>
            </div>

            <!-- Withholding -->
            <div>
              <label class="text-sm font-medium mb-1 block"
                >With Withholding</label
              >
              <select
                v-model="prefillPayload.withWithholding"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>

            <!-- Check Number -->
            <div>
              <label class="text-sm font-medium mb-1 block">Check Number</label>
              <input
                v-model="prefillPayload.checkNo"
                type="text"
                placeholder="Check Number"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- Bank -->
            <div>
              <label class="text-sm font-medium mb-1 block">Bank</label>
              <input
                v-model="prefillPayload.bank"
                placeholder="Bank"
                class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- VATable Sales -->
            <div>
              <label class="text-sm font-medium mb-1 block"
                >VATable Sales</label
              >
              <input
                v-model.number="prefillPayload.vatableSalesRaw"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- VAT Exempt Sales -->
            <div>
              <label class="text-sm font-medium mb-1 block"
                >VAT Exempt Sales</label
              >
              <input
                v-model.number="prefillPayload.vatExemptSales"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>

            <!-- Zero-Rated Sales -->
            <div>
              <label class="text-sm font-medium mb-1 block"
                >Zero-Rated Sales</label
              >
              <input
                v-model.number="prefillPayload.zeroRatedSales"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />
            </div>
          </div>
        </div>

        <!-- Items Section -->
        <div class="col-span-4 mt-5 border-t pt-4">
          <!-- Header row -->
          <div class="flex justify-between items-center mb-3 pb-2">
            <div class="text-lg font-semibold tracking-wide">
              Default Invoice Items
            </div>

            <!-- Add Item Toggle Button -->
            <button
              type="button"
              @click="showItemForm = !showItemForm"
              class="border border-brand_orange2 text-brand_orange2 hover:bg-brand_orange1 hover:text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              + Add Item
            </button>
          </div>

          <!-- Single Prefill Item Form -->
          <transition name="fade">
            <div
              v-if="showItemForm"
              class="flex items-center bg-gray-50 p-2 rounded-md border border-gray-200 shadow-sm gap-2 mb-3"
            >
              <!-- Description -->
              <input
                v-model="prefillPayload.invoice_items.description"
                placeholder="Description"
                class="flex-1 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />

              <!-- Quantity -->
              <input
                v-model.number="prefillPayload.invoice_items.quantity"
                type="number"
                min="1"
                placeholder="Qty"
                class="w-20 px-2 py-1 border rounded-md text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />

              <!-- Unit Price -->
              <input
                v-model.number="prefillPayload.invoice_items.unit_price"
                type="number"
                step="0.01"
                placeholder="Unit Price"
                class="w-24 px-2 py-1 border rounded-md text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              />

              <!-- Amount -->
              <input
                :value="
                  (
                    prefillPayload.invoice_items.quantity *
                    prefillPayload.invoice_items.unit_price
                  ).toFixed(2)
                "
                type="text"
                readonly
                class="w-24 px-2 py-1 border rounded-md text-right bg-gray-100 text-gray-700"
              />

              <!-- Save Button aligned right -->
              <button
                type="button"
                @click="
                  addPrefillItem(
                    prefillPayload.invoice_items.description,
                    prefillPayload.invoice_items.quantity,
                    prefillPayload.invoice_items.unit_price
                  )
                "
                class="ml-2 border border-brand_orange2 text-white bg-brand_orange2 hover:bg-brand_orange1 hover:text-white px-3 py-1 rounded-md text-sm font-medium"
              >
                Add
              </button>
            </div>
          </transition>

          <!-- Display Added Items -->
          <div class="mt-3">
            <!-- Header Row -->
            <div
              class="hidden md:flex items-center bg-gray-100 p-2 rounded-md border border-gray-200 font-semibold text-gray-700 gap-2"
            >
              <div class="flex-1">Description</div>
              <div class="w-16 text-right">Qty</div>
              <div class="w-20 text-right">Unit Price</div>
              <div class="w-24 text-right">Amount</div>
              <div class="w-24 text-center">Action</div>
            </div>

            <!-- Item Rows -->
            <div class="flex flex-col gap-1 mt-1">
              <div
                v-if="prefillPayload.invoice_items.length"
                v-for="(item, index) in prefillPayload.invoice_items"
                :key="index"
                class="flex items-center bg-white p-2 rounded-md border border-gray-200 hover:bg-gray-50 transition gap-2"
              >
                <div class="flex-1 text-gray-800 truncate">
                  {{ item.description }}
                </div>
                <div class="w-16 text-right text-gray-800">
                  {{ item.quantity }}
                </div>
                <div class="w-20 text-right text-gray-800">
                  {{ item.cost?.toFixed(2) || "0.00" }}
                </div>
                <div class="w-24 text-right text-gray-800">
                  {{ item.total?.toFixed(2) || "0.00" }}
                </div>
                <div class="w-24 flex justify-center">
                  <button
                    type="button"
                    @click="prefillPayload.invoice_items.splice(index, 1)"
                    class="border border-red-400 text-red-500 hover:bg-red-500 hover:text-white px-3 py-1 rounded-md text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <!-- No Items Yet Message -->
              <div
                v-else
                class="flex justify-center items-center bg-gray-50 p-4 rounded-md border border-gray-200 text-gray-500 italic"
              >
                No items added yet.
              </div>
            </div>
          </div>
        </div>
      </form>

      <!-- Buttons Row -->
      <div class="flex justify-end gap-4 items-center mt-6">
        <button
          @click.prevent="savePrefill()"
          type="button"
          class="border border-brand_orange2 text-white bg-brand_orange2 hover:bg-brand_orange1 hover:text-white p-3 w-1/6 rounded-md flex justify-center items-center"
        >
          Save
        </button>

        <button
          @click.prevent="clearPrefill()"
          type="button"
          class="border border-red-400 text-red-500 hover:bg-red-500 hover:text-white p-3 w-1/6 rounded-md flex justify-center items-center"
        >
          Clear
        </button>
      </div>
    </Dialog>

    <div class="w-full h-screen px-8">
      <div class="center-all w-full flex-col">
        <!-- Page Header Nav -->
        <div class="flex-start w-[1561px] mt-6">
          <p class="text-[25px] font-bold text-brand_gray3">
            Create Service Invoice
          </p>
        </div>
        <hr class="border-t-2 w-[1561px] border-brand_gray2 my-3" />
      </div>
      <form class="w-full mt-5">
        <div class="grid grid-cols-6 gap-4">
          <!-- Column 1 -->
          <SectionDataEntry
            @dataEntry="getSectionDataEntry"
            :prefillData="prefillPayload"
            :errors="errors"
          />
          <!-- Column 2 -->
          <SectionTotalSummary
            @totalSummary="getTotalSummaryEntry"
            :prefillData="prefillPayload"
            :errors="errors"
          />
        </div>
        <!-- BUTTONS -->
        <div class="flex justify-between items-center mt-3 w-full">
          <!-- Left-side Prefill button -->
          <button
            @click.prevent="showPrefill = true"
            class="bg-brand_orange2 hover:bg-brand_orange1 text-white p-3 w-1/12 rounded-md flex justify-center items-center text-sm font-medium transition-colors"
          >
            Form Prefill
          </button>

          <!-- Right-side buttons -->
          <div class="flex justify-end gap-2 w-1/4">
            <!-- Back as router-link -->
            <router-link
              to="/serviceInvoice/List"
              class="border border-brand_orange2 text-brand_orange2 hover:bg-brand_orange1 hover:text-white p-3 w-1/3 rounded-md flex justify-center items-center"
            >
              Back
            </router-link>

            <!-- Submit button -->
            <button
              :disabled="isLoading"
              @click.prevent="validateThenConfirm"
              class="bg-brand_orange2 hover:bg-brand_orange1 text-white p-3 w-1/3 rounded-md flex justify-center items-center"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { ref, inject, computed, reactive, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { invoiceSchema } from "../utils/formValidators";
import ConfirmModal from "@/components/modals/confirmation.vue";
import SectionDataEntry from "../components/partial/ServiceInvoice/SectionDataEntry.vue";
import SectionTotalSummary from "../components/partial/ServiceInvoice/SectionTotalSummary.vue";
import loading from "@/components/modals/loading.vue";

export default {
  components: {
    ConfirmModal,
    SectionDataEntry,
    SectionTotalSummary,
    loading,
  },
  setup() {
    const toast = useToast();
    const errors = ref({});
    const store = useStore();
    const router = useRouter();
    const axios = inject("$axios");
    const dataEntryPayload = ref({});
    const totalSummaryPayload = ref({});
    const serviceInvoicePayload = ref({});
    const showModal = ref(false);
    const userData = computed(() => store.state.userData);
    const isLoading = ref(false);

    const mergeNonNull = (target, source) => {
      Object.keys(source).forEach((key) => {
        if (source[key] !== null && source[key] !== undefined) {
          target[key] = source[key];
        }
      });
    };

    const getSectionDataEntry = (val) => {
      dataEntryPayload.value = { ...val };
      mergeNonNull(serviceInvoicePayload.value, dataEntryPayload.value);
    };

    const getTotalSummaryEntry = (val) => {
      totalSummaryPayload.value = { ...val };
      mergeNonNull(serviceInvoicePayload.value, totalSummaryPayload.value);
    };

    // Methods
    const validateThenConfirm = async () => {
      errors.value = {};

      try {
        // Validate first
        await invoiceSchema.validate(serviceInvoicePayload.value, {
          abortEarly: false,
        });

        // Open modal if valid
        showModal.value = true;
      } catch (err) {
        if (err.name === "ValidationError") {
          err.inner.forEach((e) => {
            errors.value[e.path] = e.message;
          });

          toast.add({
            severity: "warn",
            summary: "Validation Error",
            detail: "Please fill in the required fields.",
            life: 3000,
          });
        }
      }
    };

    const submitFormData = async () => {
      try {
        console.log("Final Payload : ", serviceInvoicePayload);

        isLoading.value = true;

        const res = await axios.post(
          "/api/invoice/create",
          serviceInvoicePayload.value
        );

        serviceInvoicePayload.value = {};
        console.log("Response:", res.data.data.transaction.id);

        const transactionId = res.data.data.transaction.id;

        router.push({
          name: "ServiceInvoiceView",
          params: { id: transactionId },
        });

        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Successfully Created Service Invoice",
          life: 3000,
        });
      } catch (err) {
        console.error("API Error:", err.response?.data || err.message);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: err.response?.data.message,
          life: 3000,
        });
      } finally {
        isLoading.value = false;
      }
    };

    const formatTIN = (value) => {
      if (!value) return "";
      const digits = value.replace(/\D/g, "");
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{0,4})/, "$1-$2-$3-$4");
    };

    // FORM PREFILL SECTION (NOT WORKING YET )
    const prefillPayload = reactive({
      invoice_items: [],
    });
    const showPrefill = ref(false);
    const showItemForm = ref(false);

    const savePrefill = () => {
      localStorage.setItem("prefillPayload", JSON.stringify(prefillPayload));

      toast.add({
        severity: "success",
        summary: "Prefill Saved",
        detail: "Form prefill data saved successfully.",
        life: 3000,
      });
    };

    const loadPrefill = () => {
      const saved = localStorage.getItem("prefillPayload");
      if (saved) {
        Object.assign(prefillPayload, JSON.parse(saved));
      }
    };

    const clearPrefill = () => {
      // Reset all fields in your prefillPayload
      prefillPayload.value = {};

      // Remove from localStorage
      localStorage.removeItem("prefillPayload");

      // Show confirmation toast
      toast.add({
        severity: "info",
        summary: "Prefill Cleared",
        detail: "All prefill data has been cleared successfully.",
        life: 3000,
      });
    };

    const addPrefillItem = (
      itemDescription = "",
      itemQuantity = 0,
      itemCost = 0
    ) => {
      const newItem = {
        description: itemDescription,
        quantity: itemQuantity,
        cost: itemCost,
        total: itemQuantity * itemCost,
      };

      if (newItem.description !== "") {
        prefillPayload.invoice_items.push(newItem);
      }

      console.log("Prefill item added:", newItem);

      // Clear the form inputs
      prefillPayload.invoice_items.description = "";
      prefillPayload.invoice_items.quantity = 0;
      prefillPayload.invoice_items.cost = 0;
    };

    onMounted(() => {
      loadPrefill();
      Object.assign(serviceInvoicePayload.value, prefillPayload);
    });

    return {
      // Form Prefil Section
      showPrefill,
      showItemForm,
      prefillPayload,
      savePrefill,
      clearPrefill,
      addPrefillItem,
      //--------------------

      // Watcher Variables
      getSectionDataEntry,
      getTotalSummaryEntry,
      // API Methods
      submitFormData,
      // Functions
      validateThenConfirm,
      // Payloads
      dataEntryPayload,
      totalSummaryPayload,
      serviceInvoicePayload,
      // Utils
      axios,
      errors,
      userData,
      showModal,
      isLoading,

      formatTIN,
    };
  },
};
</script>
